import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Image as ImageIcon, Camera, Type, Loader2, Smile, Globe, Users, Star, BarChart3, HelpCircle, Link2, Pencil, Music, Undo2, Trash2, Check, Play, Pause, Upload, Video } from "lucide-react";
import api from "@/lib/api";
import { uploadFileChunked, fileUrl } from "@/lib/uploads";
import { compressImage, videoPoster } from "@/lib/imageCompress";
import { encodeImageMeta } from "@/lib/blur";
import { STORY_BGS, bgByKey, STORY_FONTS, STORY_EMOJIS } from "@/lib/storyBg";
import { STORY_MUSIC } from "@/lib/storyMusic";
import VideoRecorder from "@/components/social/VideoRecorder";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const mkKey = () => Math.random().toString(36).slice(2, 10);
const DRAW_COLORS = ["#F3D56D", "#ffffff", "#000000", "#e23b3b", "#38b6ff", "#3ad07a", "#c77dff"];

// Visual sticker render, shared shapes (non-interactive here — draggable in creator).
function StickerVisual({ item }) {
  if (item.kind === "emoji") return <span style={{ fontSize: `${item.size}px` }}>{item.value}</span>;
  if (item.kind === "text") return <span style={{ color: item.color, fontFamily: item.font, fontSize: `${item.size}px`, textShadow: "0 1px 6px rgba(0,0,0,.5)", fontWeight: 700 }}>{item.value}</span>;
  if (item.kind === "poll") return (
    <div className="w-52 rounded-2xl bg-white/95 text-black p-3 shadow-lg">
      <div className="text-sm font-bold mb-2 text-center break-words">{item.q || "Poll"}</div>
      <div className="grid grid-cols-2 gap-1.5">
        {(item.options || []).map((o, i) => <div key={i} className="rounded-full bg-black/5 py-1.5 text-center text-xs font-semibold text-[#8a6d1f] truncate px-1">{o}</div>)}
      </div>
    </div>
  );
  if (item.kind === "question") return (
    <div className="w-52 rounded-2xl bg-gradient-to-br from-[#F3D56D] to-[#D4AF37] p-3 shadow-lg text-black">
      <div className="text-sm font-bold mb-2 text-center break-words flex items-center justify-center gap-1"><HelpCircle size={14} /> {item.q || "Ask me anything"}</div>
      <div className="rounded-lg bg-white/70 py-2 text-center text-[11px] text-black/50">Type a reply…</div>
    </div>
  );
  if (item.kind === "link") return (
    <div className="flex items-center gap-1.5 rounded-full bg-white/95 text-black px-3.5 py-2 shadow-lg text-sm font-semibold"><Link2 size={15} className="text-[#8a6d1f]" /> {item.label || "Visit link"}</div>
  );
  return null;
}

function Overlay({ item, onChange, onRemove, canvasRef }) {
  const start = (e) => {
    e.stopPropagation();
    const move = (ev) => {
      const pt = ev.touches ? ev.touches[0] : ev;
      const rect = canvasRef.current.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (pt.clientX - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (pt.clientY - rect.top) / rect.height));
      onChange({ ...item, x, y });
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  return (
    <div onPointerDown={start} data-testid={`overlay-${item.key}`}
      className="absolute -translate-x-1/2 -translate-y-1/2 cursor-move select-none touch-none group"
      style={{ left: `${item.x * 100}%`, top: `${item.y * 100}%` }}>
      <StickerVisual item={item} />
      <button onClick={(e) => { e.stopPropagation(); onRemove(item.key); }} data-testid={`overlay-remove-${item.key}`}
        className="absolute -top-2 -right-2 h-5 w-5 grid place-items-center rounded-full bg-black text-white border border-white/40"><X size={11} /></button>
    </div>
  );
}

// Freehand doodle layer. Strokes stored in normalized coords so they scale.
function DrawLayer({ active, strokes }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const w = cv.clientWidth, h = cv.clientHeight;
    cv.width = w; cv.height = h;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, w, h);
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    strokes.forEach((s) => {
      ctx.strokeStyle = s.color; ctx.lineWidth = s.size;
      ctx.beginPath();
      s.points.forEach((p, i) => { const x = p.x * w, y = p.y * h; i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
      ctx.stroke();
    });
  }, [strokes, active]);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" style={{ pointerEvents: active ? "auto" : "none" }} data-testid="story-draw-canvas" />;
}

export default function StoryCreator({ onClose, onPosted }) {
  const { t } = useI18n();
  const fr = (typeof localStorage !== "undefined" && localStorage.getItem("rc_lang") === "fr");
  const [mode, setMode] = useState("choose"); // choose | text | media
  const [media, setMedia] = useState(null);
  const [text, setText] = useState("");
  const [bg, setBg] = useState("gold");
  const [font, setFont] = useState("heading");
  const [overlays, setOverlays] = useState([]);
  const [privacy, setPrivacy] = useState("public");
  const [alsoPost, setAlsoPost] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [showRecorder, setShowRecorder] = useState(false);
  const [posting, setPosting] = useState(false);
  const [tray, setTray] = useState(null); // emoji | poll | question | link
  const [editForm, setEditForm] = useState({});
  // drawing
  const [drawMode, setDrawMode] = useState(false);
  const [strokes, setStrokes] = useState([]);
  const [brush, setBrush] = useState("#F3D56D");
  const cur = useRef(null);
  // music
  const [music, setMusic] = useState(null); // {url, title}
  const [preview, setPreview] = useState(null); // key currently previewing
  const previewRef = useRef(null);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const musicRef = useRef(null);
  const canvasRef = useRef(null);

  const pickFile = async (e) => {
    let f = e.target.files?.[0];
    if (!f) return;
    // Android WebView's file picker often returns an empty MIME type, which used to
    // misclassify videos as images (→ black screen). Detect by extension too, and
    // repair the content-type so the backend serves a playable video/* file.
    const byExt = /\.(mp4|mov|m4v|webm|3gp|3gpp|mkv|avi|ts)$/i.test(f.name || "");
    const isVideo = /^video\//.test(f.type || "") || byExt;
    if (isVideo && !/^video\//.test(f.type || "")) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      const mime = ext === "mov" ? "video/quicktime" : ext === "webm" ? "video/webm"
        : (ext === "3gp" || ext === "3gpp") ? "video/3gpp" : "video/mp4";
      try { f = new File([f], f.name, { type: mime }); } catch {}
    }
    setUploading(true); setUploadPct(0);
    try {
      let extra = {};
      if (!isVideo) { f = await compressImage(f, { maxDim: 1600, quality: 0.7 }); const meta = await encodeImageMeta(f); extra = { blur_hash: meta.blur_hash, w: meta.media_w, h: meta.media_h }; }
      let poster = null;
      if (isVideo) { const p = await videoPoster(f); if (p) { const { url } = await uploadFileChunked(p, null, "social"); poster = url; } }
      const { url } = await uploadFileChunked(f, (pct) => setUploadPct(pct), "social");
      setMedia({ url, type: isVideo ? "video" : "image", poster, ...extra });
      setMode("media");
    } catch { toast.error(t("st_upload_failed")); }
    finally { setUploading(false); setUploadPct(null); e.target.value = ""; }
  };

  // Recorded in-app (already ~240p) with preview/retake handled inside VideoRecorder.
  const addRecordedStoryVideo = async (file, posterFile) => {
    setShowRecorder(false);
    setUploading(true); setUploadPct(0);
    try {
      let poster = null;
      if (posterFile) { const { url } = await uploadFileChunked(posterFile, null, "social"); poster = url; }
      const { url } = await uploadFileChunked(file, (pct) => setUploadPct(pct), "social");
      setMedia({ url, type: "video", poster });
      setMode("media");
    } catch { toast.error(t("st_upload_failed")); }
    finally { setUploading(false); setUploadPct(null); }
  };

  const pickMusic = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const { url } = await uploadFileChunked(f, null, "social");
      setMusic({ url, title: (f.name || "Audio").replace(/\.[^.]+$/, "").slice(0, 40) });
      setTray(null);
      toast.success(t("st_music_added"));
    } catch { toast.error(t("st_upload_failed")); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const previewClip = (clip) => {
    const a = previewRef.current;
    if (preview === clip.key) { a.pause(); setPreview(null); return; }
    a.src = clip.url; a.currentTime = 0; a.play().catch(() => {}); setPreview(clip.key);
  };
  const chooseClip = (clip) => {
    if (previewRef.current) { previewRef.current.pause(); }
    setPreview(null);
    setMusic({ url: clip.url, title: clip.title });
    setTray(null);
    toast.success(t("st_music_added"));
  };

  const addOverlay = (o) => setOverlays((arr) => [...arr, { key: mkKey(), x: 0.5, y: 0.45, ...o }]);
  const removeOverlay = (k) => setOverlays((arr) => arr.filter((x) => x.key !== k));

  const openTray = (kind) => {
    setDrawMode(false);
    if (kind === "emoji") { setTray(tray === "emoji" ? null : "emoji"); return; }
    setEditForm(kind === "poll" ? { q: "", a: "Yes", b: "No" } : kind === "link" ? { url: "", label: "" } : { q: "" });
    setTray(kind);
  };
  const submitTray = () => {
    if (tray === "poll") {
      const opts = [editForm.a?.trim(), editForm.b?.trim()].filter(Boolean);
      if (opts.length < 2) { toast.error(t("st_two_options")); return; }
      addOverlay({ kind: "poll", q: editForm.q?.trim() || t("st_poll"), options: opts });
    } else if (tray === "question") {
      addOverlay({ kind: "question", q: editForm.q?.trim() || t("st_ask_me") });
    } else if (tray === "link") {
      let u = editForm.url?.trim(); if (!u) { toast.error(t("st_enter_url")); return; }
      if (!/^https?:\/\//i.test(u)) u = "https://" + u;
      addOverlay({ kind: "link", url: u, label: editForm.label?.trim() || t("st_visit_link") });
    }
    setTray(null);
  };

  // drawing pointer handlers
  const drawDown = (e) => {
    if (!drawMode) return;
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    cur.current = { color: brush, size: 6, points: [{ x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height }] };
    setStrokes((s) => [...s, cur.current]);
  };
  const drawMove = (e) => {
    if (!drawMode || !cur.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    cur.current.points.push({ x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height });
    setStrokes((s) => s.slice(0, -1).concat({ ...cur.current }));
  };
  const drawUp = () => { cur.current = null; };

  const exportDrawing = async () => {
    if (!strokes.length) return null;
    const W = 720, H = 1280;
    const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
    const ctx = cv.getContext("2d"); ctx.lineCap = "round"; ctx.lineJoin = "round";
    strokes.forEach((s) => { ctx.strokeStyle = s.color; ctx.lineWidth = s.size * 2; ctx.beginPath(); s.points.forEach((p, i) => { const x = p.x * W, y = p.y * H; i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); }); ctx.stroke(); });
    const dataUrl = cv.toDataURL("image/png");
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], "doodle.png", { type: "image/png" });
    const { url } = await uploadFileChunked(file, null, "social");
    return url;
  };

  const post = async () => {
    setPosting(true);
    try {
      const drawing = await exportDrawing();
      const payload = {
        type: mode === "text" ? "text" : media.type,
        privacy, also_post: alsoPost,
        overlays, drawing,
        music: music?.url || null, music_title: music?.title || null,
        duration: mode === "text" ? 5 : (media?.type === "video" ? 15 : 6),
      };
      if (mode === "text") { payload.text = text.trim(); payload.bg = bg; payload.text_style = { font }; }
      else { payload.media = media.url; payload.poster = media.poster; payload.blur_hash = media.blur_hash; payload.media_w = media.w; payload.media_h = media.h; payload.text = text.trim() || null; }
      const { data } = await api.post("/social/stories", payload);
      toast.success(t("st_story_posted"));
      onPosted && onPosted(data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || t("st_post_failed")); setPosting(false); }
  };

  const canPost = mode === "text" ? text.trim().length > 0 : !!media;
  const activeBg = bgByKey(bg);
  const activeFont = STORY_FONTS.find((f) => f.key === font)?.css;
  const privacyOpts = [["public", Globe, t("st_public")], ["friends", Users, t("st_friends")], ["close", Star, t("st_close_friends")]];

  const tools = [
    { k: "emoji", Icon: Smile, label: t("st_sticker") },
    { k: "poll", Icon: BarChart3, label: t("st_poll") },
    { k: "question", Icon: HelpCircle, label: t("st_ask") },
    { k: "link", Icon: Link2, label: t("st_link") },
    { k: "draw", Icon: Pencil, label: t("st_draw") },
    { k: "music", Icon: Music, label: t("st_music") },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[120] bg-black text-white flex flex-col" data-testid="story-creator">
      <input ref={galleryRef} type="file" accept="image/*,video/*" hidden onChange={pickFile} data-testid="story-gallery-input" />
      <input ref={cameraRef} type="file" accept="image/*,video/*" capture="environment" hidden onChange={pickFile} data-testid="story-camera-input" />
      <input ref={musicRef} type="file" accept="audio/*" hidden onChange={pickMusic} data-testid="story-music-input" />

      <div className="flex items-center justify-between px-4 py-3 shrink-0">
        <button onClick={onClose} data-testid="story-creator-close" className="h-9 w-9 grid place-items-center rounded-full bg-white/10"><X size={20} /></button>
        <span className="font-heading text-lg">{t("st_new_story")}</span>
        <div className="w-9" />
      </div>

      {uploadPct !== null && (
        <div className="px-4 -mt-1 mb-1 shrink-0" data-testid="story-upload-progress">
          <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
            <div className="h-full bg-[var(--rc-gold)] transition-all duration-200" style={{ width: `${uploadPct}%` }} data-testid="story-upload-progress-fill" />
          </div>
        </div>
      )}

      {mode === "choose" && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8">
          {uploading && <div className="flex items-center gap-2 text-[var(--rc-gold)]"><Loader2 className="animate-spin" size={18} /> {t("st_uploading")}</div>}
          <button onClick={() => cameraRef.current?.click()} data-testid="story-choose-camera" className="w-full max-w-xs flex items-center gap-3 rounded-2xl bg-white/10 px-5 py-4 hover:bg-white/15">
            <span className="h-11 w-11 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black"><Camera size={22} /></span> <span className="text-left">{t("st_camera")}<br /><span className="text-xs text-white/50">{t("st_camera_sub")}</span></span>
          </button>
          <button onClick={() => galleryRef.current?.click()} data-testid="story-choose-gallery" className="w-full max-w-xs flex items-center gap-3 rounded-2xl bg-white/10 px-5 py-4 hover:bg-white/15">
            <span className="h-11 w-11 grid place-items-center rounded-full bg-white/20"><ImageIcon size={22} /></span> <span className="text-left">{t("st_gallery")}<br /><span className="text-xs text-white/50">{t("st_gallery_sub")}</span></span>
          </button>
          <button onClick={() => setShowRecorder(true)} data-testid="story-choose-record" className="w-full max-w-xs flex items-center gap-3 rounded-2xl bg-white/10 px-5 py-4 hover:bg-white/15">
            <span className="h-11 w-11 grid place-items-center rounded-full bg-white/20"><Video size={22} /></span> <span className="text-left">{fr ? "Filmer une vidéo" : "Record video"}<br /><span className="text-xs text-white/50">{fr ? "Jusqu'à 2 min · aperçu avant" : "Up to 2 min · preview first"}</span></span>
          </button>
          <button onClick={() => setMode("text")} data-testid="story-choose-text" className="w-full max-w-xs flex items-center gap-3 rounded-2xl bg-white/10 px-5 py-4 hover:bg-white/15">
            <span className="h-11 w-11 grid place-items-center rounded-full" style={{ background: activeBg.css }}><Type size={22} className="text-black" /></span> <span className="text-left">{t("st_text")}<br /><span className="text-xs text-white/50">{t("st_text_sub")}</span></span>
          </button>
        </div>
      )}

      {mode !== "choose" && (
        <>
          <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center px-4 py-1">
            <div ref={canvasRef} data-testid="story-canvas"
              onPointerDown={drawDown} onPointerMove={drawMove} onPointerUp={drawUp} onPointerLeave={drawUp}
              className="relative h-full max-h-full max-w-full rounded-2xl overflow-hidden"
              style={{ aspectRatio: "9/16", background: mode === "text" ? activeBg.css : "#000", touchAction: drawMode ? "none" : "auto" }}>
              {mode === "media" && media && (media.type === "video"
                ? <video src={fileUrl(media.url)} className="absolute inset-0 w-full h-full object-cover" muted loop autoPlay playsInline />
                : <img src={fileUrl(media.url)} alt="" className="absolute inset-0 w-full h-full object-cover" />)}
              {mode === "text" && (
                <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="story-text-input"
                  placeholder={t("st_type_something")} maxLength={280} disabled={drawMode}
                  className="absolute inset-0 w-full h-full bg-transparent resize-none text-center px-6 flex items-center outline-none placeholder-black/30"
                  style={{ color: activeBg.fg, fontFamily: activeFont, fontSize: "26px", fontWeight: 700, paddingTop: "45%" }} />
              )}
              <DrawLayer active={drawMode} strokes={strokes} />
              {!drawMode && overlays.map((o) => <Overlay key={o.key} item={o} canvasRef={canvasRef} onRemove={removeOverlay} onChange={(n) => setOverlays((arr) => arr.map((x) => x.key === o.key ? n : x))} />)}
              {music && !drawMode && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-3 py-1 text-xs" data-testid="story-music-chip">
                  <Music size={12} className="text-[var(--rc-gold)]" /> <span className="truncate max-w-[160px]">{music.title}</span>
                  <button onClick={() => setMusic(null)}><X size={12} /></button>
                </div>
              )}
            </div>
          </div>

          {/* draw controls */}
          {drawMode ? (
            <div className="px-4 py-2 flex items-center gap-2 overflow-x-auto no-scrollbar shrink-0" data-testid="story-draw-tools">
              {DRAW_COLORS.map((c) => (
                <button key={c} onClick={() => setBrush(c)} data-testid={`draw-color-${c}`} className={`h-7 w-7 shrink-0 rounded-full border-2 ${brush === c ? "border-[var(--rc-gold)] scale-110" : "border-white/30"}`} style={{ background: c }} />
              ))}
              <span className="w-px h-6 bg-white/15 mx-1 shrink-0" />
              <button onClick={() => setStrokes((s) => s.slice(0, -1))} data-testid="draw-undo" className="h-8 px-3 shrink-0 rounded-full bg-white/10 flex items-center gap-1 text-xs"><Undo2 size={14} /> {t("st_undo")}</button>
              <button onClick={() => setStrokes([])} data-testid="draw-clear" className="h-8 px-3 shrink-0 rounded-full bg-white/10 flex items-center gap-1 text-xs"><Trash2 size={14} /> {t("st_clear")}</button>
              <button onClick={() => setDrawMode(false)} data-testid="draw-done" className="h-8 px-4 shrink-0 rounded-full bg-[var(--rc-gold)] text-black font-semibold flex items-center gap-1 text-xs ml-auto"><Check size={14} /> {t("st_done")}</button>
            </div>
          ) : (
            <>
              <div className="px-4 py-2 flex items-center gap-2 overflow-x-auto no-scrollbar shrink-0">
                {mode === "text" && STORY_BGS.map((b) => (
                  <button key={b.key} onClick={() => setBg(b.key)} data-testid={`story-bg-${b.key}`}
                    className={`h-8 w-8 shrink-0 rounded-full border-2 ${bg === b.key ? "border-[var(--rc-gold)]" : "border-white/20"}`} style={{ background: b.css }} />
                ))}
                {mode === "text" && STORY_FONTS.map((f) => (
                  <button key={f.key} onClick={() => setFont(f.key)} data-testid={`story-font-${f.key}`}
                    className={`px-3 py-1 shrink-0 rounded-full text-xs ${font === f.key ? "bg-[var(--rc-gold)] text-black" : "bg-white/10"}`} style={{ fontFamily: f.css }}>{f.label}</button>
                ))}
                {mode === "text" && <span className="w-px h-6 bg-white/15 mx-1 shrink-0" />}
                {tools.map(({ k, Icon, label }) => (
                  <button key={k} data-testid={`story-tool-${k}`}
                    onClick={() => k === "draw" ? setDrawMode(true) : k === "music" ? setTray(tray === "music" ? null : "music") : openTray(k)}
                    className="h-8 px-3 shrink-0 rounded-full bg-white/10 flex items-center gap-1 text-xs"><Icon size={14} /> {label}</button>
                ))}
              </div>
              {tray === "emoji" && (
                <div className="px-4 pb-2 flex gap-2 flex-wrap shrink-0" data-testid="story-emoji-tray">
                  {STORY_EMOJIS.map((em) => <button key={em} onClick={() => { addOverlay({ kind: "emoji", value: em, size: 56 }); setTray(null); }} className="text-2xl">{em}</button>)}
                </div>
              )}
              {tray === "music" && (
                <div className="px-4 pb-2 shrink-0 max-h-52 overflow-y-auto no-scrollbar" data-testid="story-music-tray">
                  <audio ref={previewRef} onEnded={() => setPreview(null)} className="hidden" />
                  {STORY_MUSIC.map((clip) => (
                    <div key={clip.key} className="flex items-center gap-2 py-1.5" data-testid={`music-clip-${clip.key}`}>
                      <button onClick={() => previewClip(clip)} data-testid={`music-preview-${clip.key}`}
                        className="h-9 w-9 shrink-0 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black">
                        {preview === clip.key ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <button onClick={() => chooseClip(clip)} data-testid={`music-select-${clip.key}`}
                        className="flex-1 text-left flex items-center gap-2 rounded-full bg-white/10 px-3 py-2 text-sm">
                        <Music size={14} className="text-[var(--rc-gold)]" /> <span className="flex-1">{clip.title}</span>
                        {music?.url === clip.url && <Check size={16} className="text-[var(--rc-gold)]" />}
                      </button>
                    </div>
                  ))}
                  <button onClick={() => musicRef.current?.click()} data-testid="music-upload-own"
                    className="mt-1 w-full flex items-center justify-center gap-2 rounded-full bg-white/10 py-2 text-sm text-white/70">
                    <Upload size={14} /> {t("st_upload_own")}
                  </button>
                </div>
              )}
            </>
          )}

          {mode === "media" && !drawMode && (
            <div className="px-4 pb-1 shrink-0">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("st_add_caption")} data-testid="story-caption-input"
                className="w-full bg-white/10 rounded-full px-4 py-2 text-sm outline-none placeholder-white/40" />
            </div>
          )}

          {!drawMode && (
          <div className="px-4 pb-5 pt-2 shrink-0 space-y-2 rc-safe-bottom">
            <div className="flex items-center gap-2">
              {privacyOpts.map(([k, Icon, l]) => (
                <button key={k} onClick={() => setPrivacy(k)} data-testid={`story-privacy-${k}`}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-full py-2 text-xs font-medium ${privacy === k ? "bg-[var(--rc-gold)]/20 text-[var(--rc-gold)] border border-[var(--rc-gold)]/40" : "bg-white/10 text-white/60"}`}>
                  <Icon size={14} /> {l}
                </button>
              ))}
            </div>
            <button onClick={() => setAlsoPost((v) => !v)} data-testid="story-alsopost" className="flex items-center gap-2 text-xs text-white/70">
              <span className={`h-4 w-4 rounded grid place-items-center border ${alsoPost ? "bg-[var(--rc-gold)] border-[var(--rc-gold)]" : "border-white/40"}`}>{alsoPost && <span className="text-black text-[10px] font-bold">✓</span>}</span>
              {t("st_also_share")}
            </button>
            <button onClick={post} disabled={!canPost || posting} data-testid="story-post-btn"
              className="w-full flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-3.5 text-lg disabled:opacity-50">
              {posting && <Loader2 size={18} className="animate-spin" />} {t("st_post_story")}
            </button>
          </div>
          )}
        </>
      )}

      {/* sticker editor sheet */}
      {(tray === "poll" || tray === "question" || tray === "link") && (
        <div className="fixed inset-0 z-[130] bg-black/70 flex items-center justify-center p-6" onClick={() => setTray(null)} data-testid="sticker-editor">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl bg-[var(--rc-surface)] text-[var(--rc-text)] p-4 space-y-3">
            <div className="font-heading text-lg text-[var(--rc-gold)]">{tray === "poll" ? t("st_add_poll") : tray === "question" ? t("st_ask_question") : t("st_add_link")}</div>
            {tray === "poll" && (<>
              <input value={editForm.q} onChange={(e) => setEditForm((f) => ({ ...f, q: e.target.value }))} placeholder={t("st_question_opt")} maxLength={60} data-testid="poll-q-input" className="w-full rounded-xl bg-[var(--rc-bg)] border border-[var(--rc-border)] px-3 py-2.5 outline-none" />
              <div className="flex gap-2">
                <input value={editForm.a} onChange={(e) => setEditForm((f) => ({ ...f, a: e.target.value }))} placeholder={t("st_option1")} maxLength={24} data-testid="poll-a-input" className="flex-1 rounded-xl bg-[var(--rc-bg)] border border-[var(--rc-border)] px-3 py-2.5 outline-none" />
                <input value={editForm.b} onChange={(e) => setEditForm((f) => ({ ...f, b: e.target.value }))} placeholder={t("st_option2")} maxLength={24} data-testid="poll-b-input" className="flex-1 rounded-xl bg-[var(--rc-bg)] border border-[var(--rc-border)] px-3 py-2.5 outline-none" />
              </div>
            </>)}
            {tray === "question" && (
              <input value={editForm.q} onChange={(e) => setEditForm((f) => ({ ...f, q: e.target.value }))} placeholder={t("st_ask_me")} maxLength={60} data-testid="question-q-input" className="w-full rounded-xl bg-[var(--rc-bg)] border border-[var(--rc-border)] px-3 py-2.5 outline-none" />
            )}
            {tray === "link" && (<>
              <input value={editForm.url} onChange={(e) => setEditForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://example.com" data-testid="link-url-input" className="w-full rounded-xl bg-[var(--rc-bg)] border border-[var(--rc-border)] px-3 py-2.5 outline-none" />
              <input value={editForm.label} onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))} placeholder={t("st_btn_label")} maxLength={24} data-testid="link-label-input" className="w-full rounded-xl bg-[var(--rc-bg)] border border-[var(--rc-border)] px-3 py-2.5 outline-none" />
            </>)}
            <div className="flex gap-2 pt-1">
              <button onClick={() => setTray(null)} className="flex-1 rounded-full bg-[var(--rc-bg)] border border-[var(--rc-border)] py-2.5 text-sm">{t("st_cancel")}</button>
              <button onClick={submitTray} data-testid="sticker-add-btn" className="flex-1 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-2.5 text-sm">{t("st_add")}</button>
            </div>
          </div>
        </div>
      )}

      {showRecorder && (
        <VideoRecorder onClose={() => setShowRecorder(false)} onUse={addRecordedStoryVideo} />
      )}
    </div>,
    document.body
  );
}
